import Alpine from 'alpinejs';
import Plyr from '../../vendor/plyr';
import './store';

export function videoPlayer(args = {}, context) {
	let video_element;
	let video_player;
	let container = this.$el;
	let controls = ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'];

	args = {
		autoplay: false,
		show_list: false,
		collapse_breakpoint: 640,
		collapsed: true,
		start: 0,
		end: 0,
		...args,
	}

	let priv = Alpine.reactive({
		source_list: null,
		current_source: 0,
		current_marker: null,
		collapsed: args.collapsed,
	});

	// Container
	if (args.container instanceof Element) {
		container = args.container;
	}
	else if (typeof args.container === 'string') {
		container = document.querySelector(args.container);
	}

	// Video element
	if (args.element instanceof Element) {
		video_element = args.element;
	}
	else if (typeof args.element === 'string') {
		video_element = container.querySelector(args.element);
	}
	else {
		video_element = container.querySelector('video');
	}

	// Controls
	if (Array.isArray(args.controls)) {
		controls = args.controls.map((el) => el.toLowerCase());
	}
	else if (args.controls instanceof Element) {
		controls = args.controls;
	}
	else if (typeof args.controls === 'string') {
		controls = container.querySelector(args.controls);
	}

	if (typeof args.exclude === 'string') {
		args.exclude = [args.exclude];
	}

	if (Array.isArray(controls) && Array.isArray(args.exclude)) {
		const exclude = args.exclude.map((el) => el.toLowerCase());
		controls = controls.filter((el) => !exclude.includes(el));
	}

	function set_player_source(source) {
		if (source && video_player) {
			video_player.source = {
				type: 'video',
				start: source.start,
				end: source.end,
				markers: source.markers,
				sources: [
					{
						src: source.src,
						provider: source.provider,
					}
				],
			};

			priv.current_marker = null;
		}
	}

	const player_manager = {
		autoplay: args.autoplay,
		show_list: args.show_list,
		collapse_breakpoint: args.collapse_breakpoint,
		should_collapse: args.collapsed,
		set element(el) {
			if (el) {
				video_element = el;
				this.init();
			}
		},
		get list() {
			return priv.source_list;
		},
		set list(list) {
			if (Array.isArray(list)) {
				priv.source_list = list.map(videoSource);
				this.restart_list();
			}
		},
		get source() {
			return this.list[priv.current_source];
		},
		set source(source) {
			priv.source_list = [videoSource(source)];
			this.restart_list();
		},
		get list_index() {
			return priv.current_source;
		},
		set list_index(i) {
			if (i >= 0 && i < priv.source_list.length) {
				if (video_player) {
					video_player.stop();
					video_player.once('ready', () => {
						video_player.play();
					});
				}
				priv.current_source = i;
				set_player_source(this.source);
			}
		},
		get is_list() {
			return this.list.length > 1;
		},
		get has_index() {
			switch (this.show_list) {
				case true:
					return true;
				case 'auto':
					return this.index_is_plural;
				default:
					return false;
			}
		},
		get index_is_plural() {
			return (this.list && this.list[0].markers.length) || this.is_list;
		},
		get has_next() {
			return priv.current_source < (priv.source_list.length - 1);
		},
		get has_prev() {
			return priv.current_source > 0;
		},
		get marker_index() {
			return priv.current_marker;
		},
		set marker_index(i) {
			if (Number.isInteger(i) && i < this.markers.length && i >= 0) {
				this.currentTime = this.markers[i].time;
				priv.current_marker = i;
			}
		},
		get markers() {
			return this.source?.markers
		},
		get currentTime() {
			return video_player?.currentTime;
		},
		set currentTime(value) {
			if (video_player) {
				video_player.currentTime = value;
			}
		},
		get playing() {
			return video_player?.playing;
		},
		set playing(value) {
			if (video_player) {
				video_player.playing = value;
			}
		},
		get ended() {
			return !this.has_next && video_player.ended;
		},
		get ready() {
			return video_player?.ready;
		},
		get can_collapse() {
			return true === this.collapse_breakpoint || this.$store.windowWidth < this.collapse_breakpoint;
		},
		get collapsed() {
			return this.can_collapse && this.should_collapse;
		},
		init() {
			if (video_element instanceof Element) {
				video_player = new Plyr(video_element, {
					controls,
					youtube: { noCookie: true },
					vimeo: { dnt: true },
					start: args.start,
					end: args.end,
					markers: {
						enabled: true, points: []
					}
				});

				video_player.on('ended', () => {
					video_player.pause();
					this.next()
				});

				video_player.on('timeupdate', () => {
					let i = this.markers.length;

					while (i--) {
						if (this.currentTime >= this.markers[i].time) {
							priv.current_marker = i;
							return;
						}
					}

					priv.current_marker = null;
				});

				if (this.autoplay) {
					video_player.once('ready', () => video_player.play());
				}

				set_player_source(this.source);
			}
		},
		play() {
			video_player?.play();
		},
		pause() {
			video_player?.pause();
		},
		stop() {
			video_player?.stop();
		},
		next() {
			this.has_next && (this.list_index = priv.current_source + 1);
		},
		prev() {
			this.has_prev && (this.list_index = priv.current_source - 1);
		},
		restart_list() {
			priv.current_source = 0;
			set_player_source(this.source);
		},
		on(event, callback) {
			video_player?.on(event, callback);
		},
		once(event, callback) {
			video_player?.once(event, callback);
		},
		set_list_item(index, marker) {
			if (index !== this.list_index) {
				this.list_index = index;
			}

			if (this.ready) {
				this.marker_index = marker;
				this.play();
			}
			else {
				this.once('timeupdate', () => {
					setTimeout(() => {
						this.marker_index = marker;
					}, 0);
				});
			}
		},
		collapse() {
			this.should_collapse = true;
		},
		uncollapse() {
			this.should_collapse = false;
		},
		toggle_collapse() {
			this.should_collapse = !this.should_collapse;
		}
	}

	player_manager.list = args.list || [args.source];

	context?.register_playable && context.register_playable(player_manager);

	return player_manager;
}

export function videoSource(args = {}) {
	const result = {
		title: args.title || '',
		src: args.src || '',
		provider: args.provider || '',
		start: args.start || 0,
		end: args.end || 0,
		markers: [],
	};

	if (Array.isArray(args.markers)) {
		result.markers = args.markers;
	}

	return result;
}